import { useEffect, useRef } from 'react';

import type { DealItemsActionType } from '@/lib/deal-events.types';
import { getDealEventsUrl } from '@/lib/config';
import { createDealSseAnalytics } from '@/lib/sse-analytics';
import { readSseFrames } from '@/lib/streaming/sse';
import { parseDealEvent } from '@/lib/utils/deal-event-stream';
import { onHiddenFor, waitForVisible } from '@/lib/utils/page-visibility';
import { useAuth0 } from '@auth0/auth0-react';

type DealEventStreamHandlers = {
  /**
   * A deal item changed — reload the deal items list. `action` carries the
   * backend action type for `ITEMS` signals; it is `undefined` for the `SYNC`
   * reload-everything signal.
   *
   * `itemIds` names the items to reload, `deletedItemIds` the ones to drop without a
   * read. Both empty means the server did not name them (bulk change) and everything
   * must be reloaded — as on `SYNC`.
   */
  onItems?: (
    action?: DealItemsActionType,
    itemIds?: string[],
    deletedItemIds?: string[]
  ) => void;
  /** The deal itself changed — re-query `getDeal`. Also fires on `SYNC`. */
  onDeal?: () => void;
  /**
   * Extra local gate on top of the global feature flag. Defaults to `true`.
   * Lets a caller keep the stream closed while some precondition isn't met.
   */
  enabled?: boolean;
};

// Reconnect backoff. The backend gates the endpoint on auth + deal access, so a
// dropped connection is usually transient (idle proxy, redeploy) — retry with a
// capped exponential backoff.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Durée pendant laquelle l'onglet doit rester caché avant que le flux soit
 * fermé (Page Visibility API). Il est rouvert dès que l'onglet redevient
 * visible, et le `SYNC` de reconnexion rattrape ce qui a été manqué.
 *
 * Pas immédiat : chaque réouverture coûte un `SYNC`, donc une relecture complète
 * du deal et de ses items — un alt-tab de quelques secondes ne doit pas la
 * provoquer. Bien en deçà du timeout de 15 min de `deals-websocket` : au-delà,
 * le service couperait de toute façon le flux d'un onglet caché, et la
 * reconnexion automatique rouvrirait un flux que personne ne regarde.
 */
export const SSE_HIDDEN_GRACE_MS = 60_000;

/**
 * Le compteur d'essais n'est réarmé qu'à la réception d'une frame, pas à
 * l'ouverture du socket : un serveur qui accepte puis ferme aussitôt (déploiement
 * progressif, proxy à timeout court, pod en fin de vie) maintiendrait sinon le
 * délai à sa valeur de base et reconnecterait une fois par seconde indéfiniment —
 * et chaque reconnexion émet un `SYNC`, donc une requête deal complète plus un
 * re-stream intégral des items.
 */
function backoffDelay(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
}

/**
 * Attente interruptible. Le listener est retiré sur les deux sorties : sans ça, le
 * chemin normal (timer qui expire) en laisserait un accroché au signal pour toute
 * la durée de vie de l'effet, soit un par reconnexion.
 */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Subscribe to a deal's SSE "signal" stream (`GET /deals/:dealId/events`) and
 * turn each `{ kind }` notification into a GraphQL refetch.
 *
 * Instead of pushing full deal / item payloads, the server pushes a lightweight
 * signal and the client pulls fresh data through the queries it already owns.
 *
 * The stream is read over `fetch` (not `EventSource`) because the backend
 * authenticates via the `Authorization` header, which `EventSource` cannot set.
 * The token is sent as `Bearer <token>`, matching the deal-items SSE stream
 * (`stream-deal-items.ts`) — same backend, same `$auth` plugin.
 */
export function useDealEventStream(
  dealId: string | undefined,
  { onItems, onDeal, enabled = true }: DealEventStreamHandlers
): void {
  const { getAccessTokenSilently } = useAuth0();

  // Keep the latest handlers in refs so changing their identity (a common case
  // with inline arrow functions) does not tear down and reopen the stream.
  const onItemsRef = useRef(onItems);
  const onDealRef = useRef(onDeal);
  onItemsRef.current = onItems;
  onDealRef.current = onDeal;

  const active = enabled && Boolean(dealId);

  useEffect(() => {
    if (!active || !dealId) return;

    const abort = new AbortController();
    const { signal } = abort;
    const analytics = createDealSseAnalytics(dealId);

    const dispatch = (frameData: string): void => {
      const parsed = parseDealEvent(frameData);
      if (!parsed) return;
      switch (parsed.kind) {
        case 'SYNC':
          // (Re)connect catch-up — reload everything.
          onDealRef.current?.();
          onItemsRef.current?.();
          break;
        case 'DEAL':
          onDealRef.current?.();
          break;
        case 'ITEMS':
          onItemsRef.current?.(
            parsed.action,
            parsed.itemIds,
            parsed.deletedItemIds
          );
          break;
      }
    };

    /**
     * Onglet caché depuis `SSE_HIDDEN_GRACE_MS` : la connexion en cours est
     * fermée et la boucle attend que l'onglet soit visible avant de rouvrir.
     * Chaque tentative a son propre `AbortController`, relié à celui de l'effet,
     * pour pouvoir fermer *une* connexion sans arrêter la boucle.
     */
    let paused = false;
    let connection: AbortController | null = null;
    const stopWatchingVisibility = onHiddenFor(SSE_HIDDEN_GRACE_MS, () => {
      paused = true;
      analytics.onPaused();
      connection?.abort();
    });

    const run = async (): Promise<void> => {
      let attempt = 0;
      while (!signal.aborted) {
        if (paused) {
          await waitForVisible(signal);
          if (signal.aborted) return;
          paused = false;
          // Reprise, pas reconnexion après échec : on repart sans délai.
          attempt = 0;
          analytics.onResumed();
        }

        const current = new AbortController();
        connection = current;
        const onAbort = (): void => current.abort();
        signal.addEventListener('abort', onAbort, { once: true });
        let connected = false;
        try {
          const token = await getAccessTokenSilently();
          if (current.signal.aborted) continue;

          const response = await fetch(getDealEventsUrl(dealId), {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
            },
            signal: current.signal,
          });
          if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed (${response.status})`);
          }

          connected = true;
          analytics.onConnected();
          await readSseFrames(response.body, (frame) => {
            attempt = 0;
            dispatch(frame.data);
          });
          // read() completed without error → the server closed the stream.
          if (!paused) analytics.onDropped('ended');
        } catch {
          // AbortError (unmount / dealId change / hidden tab) exits via the
          // guards; a mid-stream failure is a drop; a failure before
          // `connected` is a connect attempt that just retries below.
          if (signal.aborted) return;
          if (connected && !paused) analytics.onDropped('error');
        } finally {
          signal.removeEventListener('abort', onAbort);
          connection = null;
        }

        if (signal.aborted) return;
        if (paused) continue;
        attempt += 1;
        await sleep(backoffDelay(attempt), signal);
      }
    };

    void run();

    return () => {
      stopWatchingVisibility();
      abort.abort();
    };
  }, [active, dealId, getAccessTokenSilently]);
}
